import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SidebarShell, BrandMark, Button, useToast } from '@invested/ui';
import * as api from '../../services/api';
import { useAuth } from '../auth/AuthContext';
import type { Project, ProjectStatus } from '../../types';
import { PROJECT_STATUS_META, PROJECT_STATUS_ORDER } from '../../lib/statusMeta';
import { ProjectDetailPanel } from './ProjectDetailPanel';
import { NewProjectModal } from './NewProjectModal';

const ALL = '__all__';

function needsAttention(p: Project): boolean {
  return (
    (p.openBlockers ?? 0) > 0 ||
    (p.openWaiting ?? 0) > 0 ||
    p.status === 'blocked' ||
    p.status === 'waiting'
  );
}

function formatAge(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${Math.max(1, min)}m`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}t`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mnd`;
}

function StatusPill({ status }: { status: ProjectStatus }) {
  const meta = PROJECT_STATUS_META[status];
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.className}`}>
      {meta.label}
    </span>
  );
}

export function ProjectsPage() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [attention, setAttention] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  // Serialises reorder saves so rapid moves never race each other.
  const orderQueue = useRef<Promise<void>>(Promise.resolve());

  const load = useCallback(async () => {
    try {
      const data = await api.getProjects();
      setProjects(data);
    } catch {
      toast.danger('Kunne ikke laste prosjekter.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of projects) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    return Array.from(counts.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [projects]);

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of projects) counts.set(p.status, (counts.get(p.status) ?? 0) + 1);
    return counts;
  }, [projects]);

  const attentionCount = useMemo(() => projects.filter(needsAttention).length, [projects]);

  const filtersActive = attention || category !== ALL || status !== ALL || search.trim() !== '';

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (attention && !needsAttention(p)) return false;
      if (category !== ALL && p.category !== category) return false;
      if (status !== ALL && p.status !== status) return false;
      if (q) {
        const hay = [p.name, p.slug, p.category, p.currentStage ?? '', p.description ?? '', p.nextCheckpoint ?? '']
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [projects, attention, category, status, search]);

  function resetFilters() {
    setAttention(false);
    setCategory(ALL);
    setStatus(ALL);
    setSearch('');
  }

  // --- Reorder persistence (serialised) --------------------------------
  const persistOrder = useCallback(
    (orderedIds: string[]) => {
      orderQueue.current = orderQueue.current
        .then(() => api.reorderProjects(orderedIds))
        .catch(() => {
          toast.danger('Kunne ikke lagre ny rekkefølge.');
          void load();
        });
    },
    [toast, load],
  );

  const applyReorder = useCallback(
    (from: number, to: number) => {
      if (from === to || from < 0 || to < 0) return;
      setProjects((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        const renumbered = next.map((p, i) => ({ ...p, priorityOrder: i }));
        persistOrder(renumbered.map((p) => p.id));
        return renumbered;
      });
    },
    [persistOrder],
  );

  // Drag-drop (native HTML5, only when the list is unfiltered).
  const onDrop = useCallback(
    (targetId: string) => {
      if (!dragId || dragId === targetId) return;
      const from = projects.findIndex((p) => p.id === dragId);
      const to = projects.findIndex((p) => p.id === targetId);
      setDragId(null);
      applyReorder(from, to);
    },
    [dragId, projects, applyReorder],
  );

  // Keyboard reorder — move a project one slot up/down among all projects.
  const move = useCallback(
    (id: string, dir: -1 | 1) => {
      const idx = projects.findIndex((p) => p.id === id);
      applyReorder(idx, idx + dir);
    },
    [projects, applyReorder],
  );

  const sidebar = (
    <div className="flex h-full flex-col gap-5">
      <div className="px-2">
        <BrandMark size="sm" />
        <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.5))]">
          prosjekthub
        </p>
      </div>

      <Button size="sm" onClick={() => setShowNew(true)} className="mx-2">
        + Nytt prosjekt
      </Button>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-2">
        <SidebarButton
          active={attention}
          onClick={() => setAttention((v) => !v)}
          label="⚠ Trenger oppmerksomhet"
          count={attentionCount}
        />
        {filtersActive && (
          <button
            type="button"
            onClick={resetFilters}
            className="mb-1 self-start px-3 text-[11px] text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.55))] underline hover:text-white"
          >
            Nullstill filtre
          </button>
        )}

        <p className="mt-2 mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.4))]">
          Kategori
        </p>
        <SidebarButton active={category === ALL} onClick={() => setCategory(ALL)} label="Alle kategorier" count={projects.length} />
        {categories.map(([cat, count]) => (
          <SidebarButton key={cat} active={category === cat} onClick={() => setCategory(cat)} label={cat} count={count} />
        ))}

        <p className="mt-4 mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.4))]">
          Status
        </p>
        <SidebarButton active={status === ALL} onClick={() => setStatus(ALL)} label="Alle statuser" count={projects.length} />
        {PROJECT_STATUS_ORDER.map((s) => (
          <SidebarButton
            key={s}
            active={status === s}
            onClick={() => setStatus(s)}
            label={PROJECT_STATUS_META[s].label}
            count={statusCounts.get(s) ?? 0}
          />
        ))}
      </nav>

      {user && (
        <div className="space-y-1 border-t border-[color:var(--invested-border-subtle,rgb(255_255_255_/_0.08))] px-2 pt-3 text-xs text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.55))]">
          <p className="truncate">{user.email}</p>
          <Button variant="ghost" size="sm" onClick={() => void logout()} className="w-full justify-start px-2">
            Logg ut
          </Button>
        </div>
      )}
    </div>
  );

  const reorderable = !filtersActive;

  return (
    <SidebarShell sidebar={sidebar} theme="light" ambient="none">
      <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Prosjekter</h1>
            <p className="text-sm text-black/50">
              {visible.length} av {projects.length}
              {reorderable ? ' — dra eller bruk ▲▼ for å prioritere' : ' (filtrert — reordering deaktivert)'}
            </p>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk (navn, kategori, steg…)"
            aria-label="Søk i prosjekter"
            className="w-64 rounded-xl border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-black/30"
          />
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Laster…</p>
        ) : visible.length === 0 ? (
          <p className="rounded-xl border border-dashed border-black/15 p-8 text-center text-sm text-black/40">
            Ingen prosjekter matcher.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {visible.map((p, visibleIdx) => {
              const expanded = selectedId === p.id;
              return (
                <li
                  key={p.id}
                  onDragOver={(e) => reorderable && e.preventDefault()}
                  onDrop={() => reorderable && onDrop(p.id)}
                  className={`overflow-hidden rounded-xl border bg-white transition ${
                    expanded ? 'border-black/25 shadow-sm' : 'border-black/10 hover:border-black/25 hover:shadow-sm'
                  } ${dragId === p.id ? 'opacity-50' : ''}`}
                >
                  <div className="group flex items-center gap-2 px-3 py-2.5">
                    {reorderable && (
                      <span className="flex items-center gap-0.5">
                        <span
                          draggable
                          onDragStart={() => setDragId(p.id)}
                          onDragEnd={() => setDragId(null)}
                          className="cursor-grab select-none px-1 text-black/25 group-hover:text-black/45"
                          title="Dra for å prioritere"
                          aria-hidden="true"
                        >
                          ⠿
                        </span>
                        <span className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => move(p.id, -1)}
                            disabled={visibleIdx === 0}
                            aria-label={`Flytt ${p.name} opp`}
                            className="px-0.5 text-[10px] leading-none text-black/30 hover:text-black/70 disabled:opacity-20"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            onClick={() => move(p.id, 1)}
                            disabled={visibleIdx === visible.length - 1}
                            aria-label={`Flytt ${p.name} ned`}
                            className="px-0.5 text-[10px] leading-none text-black/30 hover:text-black/70 disabled:opacity-20"
                          >
                            ▼
                          </button>
                        </span>
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setSelectedId(expanded ? null : p.id)}
                      aria-expanded={expanded}
                      aria-controls={`panel-${p.id}`}
                      className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-black/30"
                    >
                      <span
                        className={`select-none text-black/30 transition-transform ${expanded ? 'rotate-90' : ''}`}
                        aria-hidden="true"
                      >
                        ▸
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{p.name}</span>
                          <StatusPill status={p.status} />
                        </span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-black/45">
                          <span>{p.category}</span>
                          {(p.openBlockers ?? 0) > 0 && <span className="text-red-600">🔴 {p.openBlockers} blocker</span>}
                          {(p.openWaiting ?? 0) > 0 && <span className="text-amber-600">⏳ {p.openWaiting} venter</span>}
                          {p.nextCheckpoint && <span className="truncate italic">neste: {p.nextCheckpoint}</span>}
                          <span className="ml-auto shrink-0 text-black/30" title={`Sist endret ${new Date(p.updatedAt).toLocaleString('nb-NO')}`}>
                            {formatAge(p.updatedAt)}
                          </span>
                        </span>
                      </span>
                    </button>
                  </div>
                  {expanded && (
                    <div id={`panel-${p.id}`}>
                      <ProjectDetailPanel
                        projectId={p.id}
                        onChanged={() => void load()}
                        onDeleted={() => {
                          setSelectedId(null);
                          void load();
                        }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            void load();
          }}
        />
      )}
    </SidebarShell>
  );
}

function SidebarButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-sm transition-colors ${
        active
          ? 'bg-[color:var(--invested-bg-elevated-hover,rgb(255_255_255_/_0.10))] text-[color:var(--invested-text-primary,white)]'
          : 'text-[color:var(--invested-text-muted,rgb(255_255_255_/_0.65))] hover:bg-[color:var(--invested-bg-elevated,rgb(255_255_255_/_0.05))]'
      }`}
    >
      <span className="truncate">{label}</span>
      {count !== undefined && <span className="ml-2 text-xs opacity-60">{count}</span>}
    </button>
  );
}
